import React, { useState } from "react";
import { Reveal, Rich, AmbientCanvas, BGMark, BGOrnament, LaunchCountdown } from "./primitives";

/* ———— 1. logo_splash ———— */
export function BlockLogoSplash({ inhoud, logo }) {
  const [mounted, setMounted] = useState(false);
  const [nu, setNu] = useState(Date.now());
  React.useEffect(() => {
    setMounted(true);
    const i = setInterval(() => setNu(Date.now()), 30000);
    return () => clearInterval(i);
  }, []);
  const tijd = (iso) => (iso ? new Date(iso).getTime() : NaN);
  const voorbij = nu >= tijd(inhoud.aftel_datum);
  const gewisseld = !!inhoud.aftel_datum_volgende && !!inhoud.aftel_wissel_op && nu >= tijd(inhoud.aftel_wissel_op);
  const datumRegel = gewisseld && inhoud.datum_regel_volgende ? inhoud.datum_regel_volgende : inhoud.datum_regel;
  const locatieRegel = gewisseld && inhoud.locatie_regel_volgende ? inhoud.locatie_regel_volgende : inhoud.locatie_regel;
  const klokDoel = gewisseld ? inhoud.aftel_datum_volgende : inhoud.aftel_datum;
  const klokTonen = inhoud.aftelklok_aan && (gewisseld || !voorbij || !inhoud.aftel_wissel_op);
  const vignette = "radial-gradient(ellipse at 50% 65%, rgba(201,169,97,0.09) 0%, rgba(0,0,0,0) 55%)";
  return (
    <section data-testid="block-logo_splash" style={{
      position: "relative", height: "100vh", minHeight: 620, overflow: "hidden",
      background: "var(--ink)", display: "flex", flexDirection: "column",
    }}>
      <AmbientCanvas mode="dust" />
      <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", background: vignette }} />
      <div style={{
        flex: 1, position: "relative", zIndex: 2,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "clamp(40px, 6vh, 80px) clamp(24px, 4vw, 48px) 0",
      }}>
        {logo ? (
          <img src={logo.url} alt={logo.alt || "Songwriters Wall of Fame"} style={{
            height: "min(38vh, 60vw)", maxHeight: "min(38vh, 380px)", width: "auto",
            maxWidth: "72vw", objectFit: "contain",
            filter: "drop-shadow(0 0 80px rgba(201,169,97,0.22))",
            opacity: mounted ? 1 : 0, transform: mounted ? "scale(1)" : "scale(1.04)",
            transition: "opacity 2.2s var(--ease-slow), transform 2.2s var(--ease-slow)",
          }} />
        ) : (
          <div style={{
            textAlign: "center", opacity: mounted ? 1 : 0,
            transition: "opacity 2.2s var(--ease-slow)",
          }}>
            <div style={{
              fontFamily: "var(--serif)", fontSize: "clamp(44px, 8vw, 116px)",
              lineHeight: 0.95, letterSpacing: "-0.02em", color: "var(--cream)",
              filter: "drop-shadow(0 0 80px rgba(201,169,97,0.22))",
            }}>
              Songwriters<br /><em style={{ color: "var(--gold)" }}>Wall of Fame</em>
            </div>
          </div>
        )}
      </div>
      <div style={{
        position: "relative", zIndex: 3,
        padding: "0 clamp(24px, 4vw, 72px) clamp(48px, 7vh, 96px)",
        maxWidth: 1440, margin: "0 auto", width: "100%",
        opacity: mounted ? 1 : 0, transition: "opacity 1.4s var(--ease) 1s",
      }}>
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          gap: 28, paddingTop: 36, borderTop: "1px solid var(--line)",
        }}>
          <div style={{
            fontFamily: "var(--serif)", fontSize: "clamp(22px, 2.2vw, 34px)",
            lineHeight: 1.25, letterSpacing: "-0.01em", color: "var(--cream)",
            textAlign: "center", fontWeight: 400,
          }}>
            <Rich as="span" html={datumRegel} />
            <span style={{ color: "var(--gold)", margin: "0 0.5em" }}>·</span>
            <Rich as="em" html={locatieRegel} style={{ color: "rgba(243,234,215,0.82)" }} />{" "}
            <BGMark size={14} color="var(--gold)" opacity={0.75} />
          </div>
          {klokTonen && <LaunchCountdown key={klokDoel} target={klokDoel} />}
          <div className="swof-scroll-cue" style={{
            marginTop: 4, display: "flex", alignItems: "center", gap: 14, color: "var(--gold)",
            fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.28em",
            textTransform: "uppercase", animation: "swofScrollCue 2.4s var(--ease) infinite",
          }}>
            <span style={{ width: 18, height: 1, background: "var(--gold)", opacity: 0.5 }} />
            <span>{inhoud.scroll_cue}</span>
            <span>↓</span>
            <span style={{ width: 18, height: 1, background: "var(--gold)", opacity: 0.5 }} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ———— 2. hero ———— */
export function BlockHero({ inhoud }) {
  const tagline = inhoud.tagline || "";
  const words = tagline.split(" ");
  return (
    <section data-testid="block-hero" style={{ position: "relative", minHeight: "100vh", overflow: "hidden", paddingTop: 0 }}>
      <AmbientCanvas mode="dust" />
      <div style={{
        position: "relative", zIndex: 2, minHeight: "100vh",
        padding: "140px clamp(24px, 4vw, 72px) 72px",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        maxWidth: 1440, margin: "0 auto",
      }}>
        <Reveal>
          <div style={{ maxWidth: "18ch" }}>
            <div className="eyebrow" style={{ marginBottom: 32 }}>{inhoud.kleine_kop}</div>
            <h1 className="display" style={{ fontSize: "clamp(64px, 9vw, 148px)", margin: 0, fontWeight: 400 }}>
              {words.map((w, i) => (
                <span key={i}>
                  {w}{i === Math.floor(words.length / 2) - 1 ? <br /> : " "}
                </span>
              ))}
            </h1>
          </div>
        </Reveal>
        <Reveal>
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 32,
            alignItems: "end", paddingTop: 48, borderTop: "1px solid var(--line)", marginTop: 48,
          }}>
            <div style={{ gridColumn: "span 5" }}>
              <Rich html={inhoud.lede} className="swof-body" style={{
                fontFamily: "var(--serif)", fontSize: "clamp(17px, 1.4vw, 22px)",
                lineHeight: 1.5, margin: 0, color: "var(--cream)", maxWidth: "42ch",
                opacity: 0.85, textWrap: "pretty",
              }} />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ———— 3. waarom ———— */
export function BlockWaarom({ inhoud }) {
  const isDark = inhoud.achtergrond === "donker";
  const line = isDark ? "var(--line)" : "var(--line-cream)";
  const gold = isDark ? "var(--gold)" : "var(--gold-deep)";
  const sub = isDark ? "rgba(243,234,215,0.75)" : "rgba(26,22,17,0.75)";
  return (
    <section id="waarom" data-testid="block-waarom" className={isDark ? "" : "tone-cream"} style={{
      background: isDark ? "var(--ink)" : "var(--cream)",
      color: isDark ? "var(--cream)" : "var(--cream-ink)",
      padding: "clamp(100px, 12vw, 180px) 0",
    }}>
      <div className="container">
        <Reveal>
          <div style={{ display: "flex", alignItems: "baseline", gap: 32, marginBottom: 48, flexWrap: "wrap" }}>
            <div className="eyebrow" style={{ color: gold }}>{inhoud.kleine_kop}</div>
            <div style={{ flex: 1, height: 1, background: line, minWidth: 40 }} />
          </div>
          <Rich as="h2" html={inhoud.kop} className="display swof-kop" style={{
            fontSize: "clamp(72px, 14vw, 240px)", lineHeight: 0.9, letterSpacing: "-0.035em",
            margin: "0 0 clamp(40px, 5vw, 72px)", fontWeight: 400, whiteSpace: "nowrap",
          }} />
        </Reveal>
        <Reveal>
          <div style={{ margin: "0 0 clamp(56px, 7vw, 96px)" }}>
            <BGOrnament tone={isDark ? "gold" : "gold-deep"} size={16} align="left" label={inhoud.ornament_label} />
          </div>
        </Reveal>
        <Reveal>
          <Rich as="p" html={inhoud.lede} style={{
            maxWidth: "30ch", margin: "0 0 clamp(64px, 8vw, 112px)",
            fontFamily: "var(--serif)", fontStyle: "italic",
            fontSize: "clamp(32px, 3.4vw, 52px)", lineHeight: 1.15, letterSpacing: "-0.015em",
            color: isDark ? "var(--cream)" : "var(--cream-ink)",
          }} />
        </Reveal>
        <Reveal>
          <Rich html={inhoud.body} className="swof-body swof-body--lead swof-body-2col" style={{
            columnCount: 2, columnGap: "clamp(40px, 5vw, 80px)", fontFamily: "var(--serif)",
            fontSize: "clamp(18px, 1.45vw, 22px)", lineHeight: 1.6, color: sub, textWrap: "pretty",
          }} />
        </Reveal>
        <Reveal>
          <div style={{
            marginTop: "clamp(64px, 8vw, 120px)", paddingTop: 40, borderTop: `1px solid ${line}`,
            display: "flex", justifyContent: "flex-end",
          }}>
            <Rich as="p" html={inhoud.citaat} style={{
              margin: 0, maxWidth: "24ch", fontFamily: "var(--serif)", fontStyle: "italic",
              fontSize: "clamp(28px, 2.8vw, 44px)", lineHeight: 1.2, letterSpacing: "-0.01em",
              color: gold, textAlign: "right",
            }} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ———— 4. video ———— */
export function BlockVideo({ inhoud, video }) {
  const [playing, setPlaying] = useState(false);
  const [hover, setHover] = useState(false);
  const line = "var(--line)";
  const externeId = video?.externe_id;
  const isYouTube = !video || video.bron === "youtube";
  const posterSrc = isYouTube && externeId ? `https://i.ytimg.com/vi/${externeId}/maxresdefault.jpg` : null;
  const embedSrc = () => {
    if (!isYouTube) return `https://iframe.videodelivery.net/${externeId}?autoplay=true`;
    const origin = /^https?:/.test(window.location.origin)
      ? `&origin=${encodeURIComponent(window.location.origin)}&enablejsapi=1` : "";
    return `https://www.youtube-nocookie.com/embed/${externeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&color=white${origin}`;
  };
  const watchUrl = isYouTube ? `https://www.youtube.com/watch?v=${externeId}` : "#";

  return (
    <section id="video" data-testid="block-video" style={{
      background: "var(--ink)", color: "var(--cream)", borderTop: `1px solid ${line}`,
    }}>
      <div className="container" style={{ padding: "clamp(80px, 11vw, 140px) clamp(24px, 4vw, 72px)" }}>
        <Reveal>
          <div style={{
            display: "flex", alignItems: "baseline", gap: 18, marginBottom: "clamp(28px, 4vw, 48px)",
            fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.24em",
            textTransform: "uppercase", color: "var(--gold)",
          }}>
            <span>{inhoud.kleine_kop}</span>
            <span style={{ flex: 1, height: 1, background: line }} />
          </div>
        </Reveal>
        <Reveal>
          <div style={{
            position: "relative", width: "100%", aspectRatio: "16 / 9",
            border: "1px solid var(--line-strong)", background: "#000", overflow: "hidden",
            boxShadow: "0 40px 120px rgba(0,0,0,0.55)",
          }}>
            {playing ? (
              <iframe title={video?.titel || "Video"} src={embedSrc()}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                referrerPolicy="origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen />
            ) : (
              <button data-testid="video-play" onClick={() => setPlaying(true)}
                onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                aria-label="Speel video af" style={{
                  position: "absolute", inset: 0, width: "100%", height: "100%",
                  border: 0, padding: 0, cursor: "pointer", display: "block",
                  background: `linear-gradient(180deg, rgba(5,4,3,0.15) 0%, rgba(5,4,3,0.35) 55%, rgba(5,4,3,0.72) 100%)${posterSrc ? `, url(${posterSrc}) center/cover no-repeat` : ""}`,
                }}>
                <span aria-hidden style={{
                  position: "absolute", inset: 0,
                  background: "radial-gradient(ellipse at 50% 50%, rgba(201,169,97,0.10) 0%, rgba(0,0,0,0) 60%)",
                }} />
                <span style={{
                  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                  width: "clamp(72px, 9vw, 108px)", height: "clamp(72px, 9vw, 108px)", borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1.5px solid var(--gold)",
                  background: hover ? "var(--gold)" : "rgba(5,4,3,0.45)",
                  backdropFilter: "blur(2px)",
                  transition: "background .45s var(--ease), transform .45s var(--ease)",
                  boxShadow: hover ? "0 0 60px rgba(201,169,97,0.45)" : "0 0 40px rgba(201,169,97,0.18)",
                  transformOrigin: "center", scale: hover ? "1.06" : "1",
                }}>
                  <span aria-hidden style={{
                    width: 0, height: 0, marginLeft: "14%",
                    borderTop: "clamp(13px, 1.6vw, 19px) solid transparent",
                    borderBottom: "clamp(13px, 1.6vw, 19px) solid transparent",
                    borderLeft: `clamp(22px, 2.7vw, 32px) solid ${hover ? "var(--ink)" : "var(--gold)"}`,
                    transition: "border-color .45s var(--ease)",
                  }} />
                </span>
                <span style={{
                  position: "absolute", left: "clamp(20px, 3vw, 36px)", bottom: "clamp(18px, 3vw, 32px)",
                  fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.2em",
                  textTransform: "uppercase", color: "var(--cream)", opacity: 0.85,
                }}>{inhoud.caption}</span>
              </button>
            )}
          </div>
        </Reveal>
        <Reveal>
          <div style={{ marginTop: "clamp(16px, 2vw, 24px)", display: "flex", justifyContent: "flex-end" }}>
            <Rich as="a" html={inhoud.onder_regel} href={watchUrl} target="_blank" rel="noopener noreferrer" style={{
              fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: "0.18em",
              textTransform: "uppercase", color: "var(--gold)", textDecoration: "none", opacity: 0.7,
            }} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ———— 5. onderscheiding ———— */
export function BlockOnderscheiding({ inhoud }) {
  const line = "var(--line)";
  const gold = "var(--gold)";
  return (
    <section id="onderscheiding" data-testid="block-onderscheiding" style={{
      background: "var(--ink)", color: "var(--cream)",
      padding: "clamp(100px, 12vw, 180px) 0", borderTop: `1px solid ${line}`,
    }}>
      <div className="container">
        <Reveal>
          <div style={{ display: "flex", alignItems: "baseline", gap: 32, marginBottom: 48, flexWrap: "wrap" }}>
            <div className="eyebrow" style={{ color: gold }}>{inhoud.kleine_kop}</div>
            <div style={{ flex: 1, height: 1, background: line, minWidth: 40 }} />
          </div>
          <Rich as="h2" html={inhoud.kop} className="display swof-kop" style={{
            fontSize: "clamp(64px, 11vw, 200px)", lineHeight: 0.92, letterSpacing: "-0.035em",
            margin: "0 0 clamp(40px, 5vw, 72px)", fontWeight: 400,
          }} />
        </Reveal>
        <Reveal>
          <Rich as="p" html={inhoud.lede} style={{
            maxWidth: "32ch", margin: "0 0 clamp(64px, 8vw, 112px)",
            fontFamily: "var(--serif)", fontStyle: "italic",
            fontSize: "clamp(30px, 3.2vw, 48px)", lineHeight: 1.18,
            letterSpacing: "-0.015em", color: "var(--cream)",
          }} />
        </Reveal>
        <Reveal>
          <Rich html={inhoud.body} className="onderscheiding-body swof-body swof-body--lead" style={{
            columnCount: 2, columnGap: "clamp(40px, 5vw, 80px)", fontFamily: "var(--serif)",
            fontSize: "clamp(18px, 1.45vw, 22px)", lineHeight: 1.6,
            color: "rgba(243,234,215,0.75)", textWrap: "pretty",
          }} />
        </Reveal>
        <Reveal>
          <div style={{
            marginTop: "clamp(64px, 8vw, 120px)", paddingTop: 32, borderTop: `1px solid ${line}`,
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "clamp(24px, 3vw, 48px)",
          }}>
            {(inhoud.meta || []).map((m, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div className="eyebrow" style={{ color: gold, fontSize: 10 }}>{m.k}</div>
                <div style={{
                  fontFamily: "var(--serif)", fontSize: "clamp(28px, 2.6vw, 40px)",
                  lineHeight: 1, fontWeight: 400, letterSpacing: "-0.02em", color: "var(--cream)",
                }}>{m.v}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
